// api/Task/index.js
// -----------------------------------------------------------------------------
// A thin Axios wrapper for the Task service.  Mirrors the conventions used in
// other services (e.g. WaitingRoom) so the rest of the UI code can stay
// consistent.
// -----------------------------------------------------------------------------

import { axios } from "../../../lib/axios";

/**
 * Helper: convert a plain object to a URL‑encoded query‑string, skipping empty
 * or undefined values.
 */
function toQueryString(params = {}) {
  return Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
}

// ----------------------------------------------------------------------------
// READ (search / getById)
// ----------------------------------------------------------------------------

/**
 * Search tasks — server will return a FHIR Bundle of Task resources.
 *
 * Required: orgId
 * Optional: status | type | priority | owner | for (patient reference)
 *
 * Example:
 *   searchTasks({ orgId: "203845", status: "new" })
 */
export const searchTasks = (query) => {
  if (!query?.orgId) throw new Error("orgId is required to search tasks");
  return axios.get(`task?${toQueryString(query)}`);
};

/**
 * Fetch a single Task by ID + org.
 */
export const getTaskById = ({ id, orgId }) => {
  if (!id) throw new Error("id is required");
  if (!orgId) throw new Error("orgId is required");
  return axios.get(`task/${id}?orgId=${encodeURIComponent(orgId)}`);
};

// ----------------------------------------------------------------------------
// CREATE / UPDATE / DELETE
// ----------------------------------------------------------------------------

/**
 * POST /task – create a new Task.  `payload` is the full FHIR Task resource.
 */
export const createTask = (payload) => {
  return axios.post("/task", payload);
}

/**
 * PUT /task/:id – update a Task.  Supply only the fields you wish to modify.
 */
export const updateTask = ({ id, ...payload }) => {
  if (!id) throw new Error("id is required");
  return axios.put(`task/${id}`, payload);
};

/**
 * DELETE /task/:id – hard‑delete.  Requires orgId as query param for authZ.
 */
export const deleteTask = ({ id, orgId }) => {
  if (!id) throw new Error("id is required");
  if (!orgId) throw new Error("orgId is required");
  return axios.delete(`task/${id}?orgId=${encodeURIComponent(orgId)}`);
};

// ----------------------------------------------------------------------------
// Additional helpers (optional, uncomment when backend endpoints exist)
// ----------------------------------------------------------------------------
//
// export const addTaskNote = ({ id, note, actor }) =>
//   axios.post(`task/${id}/note`, { note, actor });
//
// export const assignPractitioner = ({ id, practitionerId, practitionerName }) =>
//   axios.put(`task/${id}/assign`, { practitionerId, practitionerName });
// -----------------------------------------------------------------------------
